import type { EditionSummary } from '@koderek/shared';
import { Button, Center, Loader, SegmentedControl, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ErrorAlert } from '../../components/ErrorAlert';
import { t } from '../../texts/pl';
import { dayAndMonth, editionPlaceLabel } from './format';
import { useEditions, useEventRules } from './queries';

/**
 * Nadchodzące (opublikowane) edycje – wybór przez `?edycja=` (przy kilku przełącznik, przy jednej od razu ona).
 * Wspólne dla „Naszego planu” i „Kodu QR”. `onlyRegistered` – tylko edycje z zapisami rodziny (plan bez zapisów jest pusty,
 * a przełączanie między pustymi planami sugerowałoby udział w kilku edycjach); `label` – pytanie nad przełącznikiem;
 * `optionLabel` – napis opcji (domyślnie „Miasto, dzień miesiąc”).
 */
export function UpcomingEditionPicker({
  children,
  onlyRegistered = false,
  label,
  empty,
  optionLabel,
}: {
  children: (edition: EditionSummary) => ReactNode;
  onlyRegistered?: boolean;
  label?: string;
  /** Komunikat, gdy nie ma żadnej edycji do pokazania. */
  empty?: string;
  optionLabel?: (edition: EditionSummary) => string;
}) {
  const [params, setParams] = useSearchParams();
  const editions = useEditions();
  // Bez kroku „zgłoszenie” (decyzja 2026‑09‑28) każda opublikowana edycja jest „nasza”; domyślnie ta, w której rodzina ma zapisy.
  // Jedna konferencja w kilku miastach tego samego dnia: gdy rodzina ma już zapisy w jednym mieście, edycje tego dnia w innych
  // miastach nie są jej potrzebne (BR-17) – nie ma ich w przełączniku. Edycje w tym samym mieście można łączyć (2026‑10‑03),
  // a przy blokadzie wyłączonej w ustawieniach (PRD §10 pkt 63) – wszystkie edycje dnia.
  const blockOtherCity = useEventRules().data?.blockOtherCitySameDay ?? true;
  const published = editions.data?.editions.filter((e) => e.status === 'published') ?? [];
  const dayCity = (e: EditionSummary) => (blockOtherCity ? `${e.date}|${e.city.trim().toLowerCase()}` : e.date);
  const chosenDays = new Set(published.filter((e) => e.registered).map((e) => e.date));
  const chosenCities = new Set(published.filter((e) => e.registered).map(dayCity));
  const upcoming = published.filter(
    (e) => e.registered || (!onlyRegistered && (!chosenDays.has(e.date) || chosenCities.has(dayCity(e)))),
  );
  const edition = upcoming.find((e) => e.slug === params.get('edycja')) ?? upcoming.find((e) => e.registered) ?? upcoming[0];

  if (editions.isPending) {
    return (
      <Center py="lg">
        <Loader aria-label="Ładowanie" />
      </Center>
    );
  }
  if (editions.isError) return <ErrorAlert error={editions.error} />;
  if (!edition) {
    return (
      <Stack gap="xs" align="flex-start">
        <Text>{empty ?? t.familyActivities.noEditions}</Text>
        <Button component={Link} to="/">
          {t.familyActivities.toEditions}
        </Button>
      </Stack>
    );
  }
  return (
    <>
      {upcoming.length > 1 && label ? (
        <Text fw={600} id="edition-picker-label" mb={-8}>
          {label}
        </Text>
      ) : null}
      {upcoming.length > 1 ? (
        <SegmentedControl
          aria-label={label ? undefined : t.familyActivities.edition}
          aria-labelledby={label ? 'edition-picker-label' : undefined}
          w="fit-content"
          // Odstęp między opcjami zamiast kresek – przy podobnych nazwach miejsc opcje się nie zlewają.
          withItemsBorders={false}
          styles={{ root: { gap: 'var(--mantine-spacing-xs)', padding: 6 } }}
          value={edition.slug}
          onChange={(slug) => setParams({ edycja: slug }, { replace: true })}
          data={upcoming.map((e) => {
            if (optionLabel) return { value: e.slug, label: optionLabel(e) };
            const { day, month } = dayAndMonth(e.date);
            return { value: e.slug, label: `${editionPlaceLabel(e, upcoming)}, ${day} ${month}` };
          })}
        />
      ) : null}
      {children(edition)}
    </>
  );
}
